import React, { useMemo, useEffect, useState, useContext } from 'react';
import classnames from 'classnames';
import Modal from 'react-modal';
import CloseIcon from '@wix/da-ds/pkg/Icons/24x24/Close';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { ScreenSizeContext } from '../../../Measured';
import { parse as parseSrcSet } from '../../../utils/srcset';
import imageStyles from './ImageFullview.scss';
function isBiggerThanViewport(width, height) {
    if (typeof window === 'undefined' || !width || !height) {
        return false;
    }
    return width > window.innerWidth || height > window.innerHeight;
}
var ZoomStage;
(function(ZoomStage) {
    ZoomStage[ZoomStage["INITIAL"] = 0] = "INITIAL";
    ZoomStage[ZoomStage["EXPANDED"] = 1] = "EXPANDED";
})(ZoomStage || (ZoomStage = {}));
export const ZoomModal = ({ src, srcSet, width, height, isPixel, onClose })=>{
    var _largestImage, _largestImage1;
    const [zoomStage, setZoomStage] = useState(ZoomStage.INITIAL);
    const isMobile = useContext(MobileContext);
    const { width: screenWidth } = useContext(ScreenSizeContext);
    const largestImage = useMemo(()=>parseSrcSet(srcSet || '').concat({
            width,
            height,
            url: src
        }).filter((source)=>source.width).sort(({ width: a = 0 }, { width: b = 0 })=>a - b).pop(), [
        srcSet,
        width,
        height,
        src
    ]);
    const maxWidth = Math.max(((_largestImage = largestImage) === null || _largestImage === void 0 ? void 0 : _largestImage.width) || 0, screenWidth);
    const canZoom = isBiggerThanViewport(width, height);
    // close when escape is used
    useEffect(()=>{
        const onKeyDown = (e)=>{
            if (e.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return ()=>window.removeEventListener('keydown', onKeyDown);
    }, [
        onClose
    ]);
    const toggleZoomStage = ()=>{
        if (canZoom) {
            // For mobile, we only want the initial zoom mode, then users can pinch+zoom all they want.
            if (zoomStage === ZoomStage.INITIAL && !isMobile) {
                setZoomStage(ZoomStage.EXPANDED);
            } else {
                setZoomStage(ZoomStage.INITIAL);
            }
        }
    };
    const isZoomed = canZoom && zoomStage === ZoomStage.EXPANDED;
    // wrapping in a modal to avoid the can of worms with scrolling the underlying page and all
    // NoLeakyScroll only goes that far, and this seems more performant overall
    return /*#__PURE__*/ React.createElement(Modal, {
        className: classnames(imageStyles['zoom-content'], zoomStage === ZoomStage.EXPANDED && imageStyles['zoom-content-expanded']),
        isOpen: true,
        overlayClassName: imageStyles['zoomed'],
        onRequestClose: onClose
    }, /*#__PURE__*/ React.createElement("button", {
        className: imageStyles['unzoom'],
        onClick: onClose
    }, /*#__PURE__*/ React.createElement(CloseIcon, {
        size: IconSize.BIG,
        className: imageStyles['unzoom-icon']
    })), /*#__PURE__*/ React.createElement("div", {
        className: imageStyles['zoom-viewport']
    }, /*#__PURE__*/ React.createElement("img", {
        alt: "",
        className: classnames(imageStyles['image'], isPixel && imageStyles['pixel'], canZoom && zoomStage === ZoomStage.INITIAL && imageStyles['initial-zoom'], canZoom && zoomStage === ZoomStage.EXPANDED && imageStyles['expanded-zoom']),
        src: isZoomed && ((_largestImage1 = largestImage) === null || _largestImage1 === void 0 ? void 0 : _largestImage1.url) ? largestImage.url : src,
        style: isZoomed ? {
            maxWidth
        } : undefined,
        onClick: toggleZoomStage
    })));
};
ZoomModal.displayName = 'ZoomModal';
export default ZoomModal;
